import { useCreate, useList } from "@refinedev/core";
import { AssetLibrary } from "../media/types";
import { useEffect, useState, useCallback, useRef } from "react";
import { sortByDate } from "helpers/utils/";
import { useSession } from "hooks/useSession";

export const useMediaAssetLibraries = ({
  projectId,
  organizationId,
}: {
  projectId?: string;
  organizationId: string | undefined;
}) => {
  const { mutateAsync: createMediaLibrary } = useCreate<AssetLibrary>();
  const [isLoading, setIsLoading] = useState(false);
  const hasAttemptedCreationRef = useRef(false);

  const { user } = useSession();

  // In prod, projects belong to clients and the user has no create rights on
  // another org's project, so auto-creating there 403s on every attempt and
  // spams the asset_libraries endpoint. Only enforce the ownership check in
  // prod — in dev/staging we have cross-org access and need to create freely.
  const canAutoCreate =
    import.meta.env.VITE_ENV !== "production" ||
    Boolean(organizationId && user?.organizations?.includes(organizationId));

  const {
    data: assetLibraries,
    isLoading: isAssetLibrariesLoading,
    isRefetching: isAssetLibrariesRefetching,
    isFetching: isAssetLibrariesFetching,
  } = useList<AssetLibrary>({
    resource: `media/${organizationId}/asset_libraries`,
    filters: [
      {
        value: projectId,
        field: "project_id",
        operator: "eq",
      },
    ],
    queryOptions: {
      enabled: !!organizationId,
    },
    errorNotification: false,
  });

  const isAssetLibrariesDataLoading = organizationId
    ? isAssetLibrariesLoading ||
      isAssetLibrariesRefetching ||
      isAssetLibrariesFetching
    : false;

  // select by default the project level asset library
  const projectAssetLibrary = assetLibraries?.data?.find((assetLib) =>
    Boolean(assetLib.project_id)
  );

  const sortedAssetLibraries =
    sortByDate(assetLibraries?.data, "created") ?? [];

  const organizationAssetLibraries = sortedAssetLibraries.filter(
    (assetLib) => assetLib !== projectAssetLibrary
  );

  // Memoize the creation function to prevent unnecessary re-renders
  // and not call it multiple times
  const createDefaultLibrary = useCallback(async () => {
    if (isLoading || hasAttemptedCreationRef.current) return;

    setIsLoading(true);
    hasAttemptedCreationRef.current = true;

    try {
      await createMediaLibrary({
        resource: `media/${organizationId}/asset_libraries`,
        values: {
          name: "Default",
          id: "-",
        },
        // done in page background
        successNotification: false,
      });
    } catch (error) {
      console.error("Failed to create media library:", error);
      // Reset the flag on error so we can retry
      hasAttemptedCreationRef.current = false;
    } finally {
      setIsLoading(false);
    }
  }, [isLoading, createMediaLibrary, organizationId]);

  useEffect(() => {
    // Only create if no organization libraries exist and we haven't attempted creation
    if (
      organizationId &&
      canAutoCreate &&
      organizationAssetLibraries.length === 0 &&
      !isLoading &&
      !hasAttemptedCreationRef.current &&
      !isAssetLibrariesDataLoading // Wait for data to load first
    ) {
      createDefaultLibrary().catch((error) => {
        console.error("Failed to create media library:", error);
      });
    }
  }, [
    organizationId,
    canAutoCreate,
    organizationAssetLibraries.length,
    isLoading,
    isAssetLibrariesDataLoading,
    createDefaultLibrary,
  ]);

  const isDataLoading = isLoading || isAssetLibrariesDataLoading;

  return {
    assetLibraries: assetLibraries?.data,
    projectAssetLibraryId: projectAssetLibrary?.id,
    projectAssetLibrary,
    organizationAssetLibraries,
    isDataLoading,
  };
};
